import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from '../components/AuthModal';
import site from '../content/site';
import { DISCOUNTS_ENABLED } from '../content/features';
import './BusinessPage.css';

// Clean, modern outline icons (not emoji) matching the site's inline-SVG
// style used elsewhere (RestaurantPage, HomePage) — consistent stroke
// weight and size across all four boxes.
function IconCustomers() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.6"/>
      <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      <path d="M15.5 5.5c1.5.4 2.5 1.7 2.5 3.2 0 1.5-1 2.8-2.5 3.2M17.5 14c2 .6 3.5 2.5 3.5 4.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
    </svg>
  );
}
function IconFree() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M11 3.5H6.5a2 2 0 00-1.4.6L3.6 5.6a2 2 0 000 2.8l9 9a2 2 0 002.8 0l6-6a2 2 0 000-2.8l-9-9a2 2 0 00-1.4-.6z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <circle cx="8.5" cy="8.5" r="1.4" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  );
}
function IconRepeat() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 12a8 8 0 0113.6-5.7M20 12a8 8 0 01-13.6 5.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      <path d="M17.5 3.5v3.2h-3.2M6.5 20.5v-3.2h3.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function IconControls() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 6h9M17 6h3M4 18h3M11 18h9M4 12h13M21 12h-1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      <circle cx="13" cy="6" r="2" fill="#fff" stroke="currentColor" strokeWidth="1.6"/>
      <circle cx="7" cy="18" r="2" fill="#fff" stroke="currentColor" strokeWidth="1.6"/>
      <circle cx="18" cy="12" r="2" fill="#fff" stroke="currentColor" strokeWidth="1.6"/>
    </svg>
  );
}

const perks = DISCOUNTS_ENABLED ? [
  { Icon: IconCustomers, title: 'Get More Customers', desc: 'Reach new customers actively looking for venues like yours.' },
  { Icon: IconFree, title: '100% Free', desc: 'Listing your venue is completely free.' },
  { Icon: IconRepeat, title: 'Increase Repeat Customers', desc: 'Turn first-time customers into loyal regulars.' },
  { Icon: IconControls, title: 'You Stay in Control', desc: 'Set the discounts that work for your business and update them anytime.' },
] : [
  { Icon: IconCustomers, title: 'Get Discovered', desc: 'Show up when people near you search, with your rating and reviews up front.' },
  { Icon: IconFree, title: '100% Free', desc: 'Listing your venue is completely free.' },
  { Icon: IconRepeat, title: 'Reply as the Owner', desc: 'Answer reviews with an Owner label so customers hear your side.' },
  { Icon: IconControls, title: 'Show Off Your Socials', desc: 'Link your TikTok right on your profile.' },
];

export default function BusinessPage() {
  const { user, isRestaurantOwner, loading } = useAuth();
  const navigate = useNavigate();
  const [authModal, setAuthModal] = useState(false);

  // A returning owner (including one with just a pending listing — role is
  // set on first submission regardless of approval status) lands straight
  // on their dashboard instead of the marketing page; "Add another
  // restaurant" from there is how they list a second location.
  useEffect(() => {
    if (!loading && isRestaurantOwner) {
      navigate('/business/dashboard', { replace: true });
    }
  }, [loading, isRestaurantOwner, navigate]);

  function handleCta() {
    if (!user) {
      setAuthModal(true);
      return;
    }
    navigate(isRestaurantOwner ? '/business/dashboard' : '/business/onboarding');
  }

  if (loading || isRestaurantOwner) {
    return (
      <main className="business-page business-page--loading">
        <div className="spinner" aria-label="Loading…" />
      </main>
    );
  }

  return (
    <>
      <main className="business-page">
        <section className="business-hero">
          <div className="business-hero__inner">
            {DISCOUNTS_ENABLED ? (
              <h1 className="business-hero__title">List your venue<br /><span>on Volcyy</span></h1>
            ) : (
              <h1 className="business-hero__title">Your venue is already<br /><span>being talked about</span></h1>
            )}
            <p className="business-hero__sub">{site.ROUTE_META['/business'].intro}</p>
            <button className="business-hero__cta" onClick={handleCta}>
              {user ? (isRestaurantOwner ? 'Go to Dashboard' : 'List Your Business') : 'Get Started'}
            </button>
          </div>
        </section>

        <section className="business-perks">
          <div className="business-perks__inner">
            <div className="business-perks__grid">
              {perks.map(p => (
                <div key={p.title} className="business-perk">
                  <span className="business-perk__icon"><p.Icon /></span>
                  <h3 className="business-perk__title">{p.title}</h3>
                  <p className="business-perk__desc">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="business-how">
          <div className="business-how__inner">
            <h2>How It Works</h2>
            <ol className="business-how__list">
              <li><strong>Create your venue listing.</strong> Enter your venue's information to get started.</li>
              <li><strong>Get approved.</strong> Our team reviews every venue listing, usually within one to two business days.</li>
              <li><strong>Start reaching new customers.</strong> Once approved, your venue goes live on Volcyy and you can manage your listing anytime from your account.</li>
            </ol>
            {!DISCOUNTS_ENABLED && (
              <p className="business-how__claim">
                <strong>Already on Volcyy?</strong> Don't create a second listing — find your venue
                and tap <em>Claim this venue</em>. Once we've verified you, it's yours to manage.
              </p>
            )}
          </div>
        </section>
      </main>

      {authModal && (
        <AuthModal
          defaultTab="signup"
          signupRole="business_owner"
          heading="Create a business account"
          onClose={() => setAuthModal(false)}
          onSuccess={() => navigate('/business/onboarding')}
        />
      )}
    </>
  );
}
